import { default as React } from 'react';
import { useState, useEffect, useRef, forwardRef, useCallback } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { githubAuthApiRef, configApiRef } from '@backstage/core-plugin-api';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Autocomplete from '@mui/material/Autocomplete';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { Octokit } from '@octokit/rest';

// GitHub repo fetch with pagination and search using Octokit
const fetchGitHubRepos = async (
  token: string,
  org: string,
  page: number,
  perPage: number,
  search?: string,
  signal?: AbortSignal,
): Promise<any[]> => {
  const octokit = new Octokit({ auth: token });

  let repos;
  if (search) {
    const res = await octokit.search.repos({
      q: `org:${org} ${search}`,
      per_page: perPage,
      page,
      sort: 'updated',
      request: { signal },
    });
    repos = res.data.items ?? [];
  } else {
    const res = await octokit.repos.listForOrg({
      org,
      type: 'all',
      per_page: perPage,
      sort: 'pushed',
      page,
      request: { signal },
    });
    repos = res.data ?? [];
  }

  return repos.map(repo => ({
    full_name: repo.full_name,
    permissions: repo.permissions,
  }));
};

// Custom ListboxComponent to support infinite scroll in dropdown
const ListboxComponent = forwardRef(function ListboxComponent(
  props: any,
  ref: any,
) {
  const { children, ...rest } = props;
  const innerRef = useRef<HTMLDivElement>(null);
  const { repos, hasMore, loading, fetchRepos } = props;

  useEffect(() => {
    if (loading) return undefined;
    if (!hasMore) return undefined;
    if (!innerRef.current) return undefined;
    const observer = new window.IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore && !loading) {
        fetchRepos();
      }
    });
    const lastChild = innerRef.current.lastElementChild;
    if (lastChild) observer.observe(lastChild);
    return () => observer.disconnect();
  }, [repos, hasMore, loading, fetchRepos]);

  return (
    <div
      ref={node => {
        if (typeof ref === 'function') ref(node);
        // Remove assignment to ref.current if it's not a function
      }}
      {...rest}
    >
      {children}
    </div>
  );
});
export const GitHubRepoSelector = ({
  onChange,
  value,
}: FieldExtensionComponentProps<{
  value?: string;
  error?: string;
}>) => {
  const [repos, setRepos] = useState<
    { label: string; value: string; permissions?: any }[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [selectedValue, setSelectedValue] = useState<{
    label: string;
    value: string;
    permissions?: any;
  } | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const githubAuth = useApi(githubAuthApiRef);
  const config = useApi(configApiRef);
  const githubOrg =
    config.getOptionalString('frontendPluginMicroservices.githubOrg') ??
    'mediwareinc';
  const perPage = 10;
  const tokenRef = useRef<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Fetch repos with pagination and search
  const fetchRepos = useCallback(
    async (reset = false, searchOverride?: string) => {
      if (!tokenRef.current) return;

      // Cancel previous request if exists
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller
      abortControllerRef.current = new AbortController();

      setLoading(true);
      try {
        const searchVal =
          searchOverride !== undefined ? searchOverride : inputValue;
        const reposData = await fetchGitHubRepos(
          tokenRef.current,
          githubOrg,
          reset ? 1 : page,
          perPage,
          searchVal,
          abortControllerRef.current.signal,
        );
        const newRepos = reposData.map((repo: any) => ({
          label: repo.full_name,
          value: repo.full_name,
          permissions: repo.permissions,
        }));
        if (reset) {
          setRepos(newRepos);
          setPage(2);
        } else {
          setRepos(prev => [...prev, ...newRepos]);
          setPage(prev => prev + 1);
        }
        setHasMore(newRepos.length === perPage);
      } catch (e) {
        // Only show error if not aborted
        if (!abortControllerRef.current?.signal.aborted) {
          onChange({
            value: inputValue,
            error: 'Error fetching GitHub repos:',
          });
        }
      } finally {
        setLoading(false);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [onChange, inputValue, githubOrg, page, perPage],
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Fetch token once and trigger initial fetch when token is ready
  useEffect(() => {
    githubAuth.getAccessToken(['repo', 'read:org']).then(token => {
      tokenRef.current = token;
      // Trigger initial fetch when token is ready
      setRepos([]);
      setPage(1);
      setHasMore(true);
      fetchRepos(true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [githubAuth, githubOrg]);

  // Debounced search effect
  useEffect(() => {
    if (!tokenRef.current) return undefined;
    const handler = setTimeout(() => {
      setRepos([]);
      setPage(1);
      setHasMore(true);
      fetchRepos(true, inputValue);
    }, 400); // 400ms debounce
    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue, githubOrg]);

  const handleInputChange = (_: any, inputVal: string, reason: string) => {
    if (reason === 'input') {
      setInputValue(inputVal);
    }
  };

  const handleChange = async (_: any, newValue: any) => {
    if (!newValue) {
      setSelectedValue(null);
      setInputValue('');
      onChange({ value: '', error: 'Must select a project' });
      return;
    }

    setSelectedValue(newValue);
    setInputValue(newValue.value);

    // Check write access
    if (newValue.permissions?.push || newValue.permissions?.admin) {
      onChange({ value: newValue.value, error: '' });
    } else {
      onChange({
        value: newValue.value,
        error: "You don't have write access to this repo",
      });
    }
  };

  // Initialize from existing value
  useEffect(() => {
    if (value?.value && repos.length > 0) {
      const existingRepo = repos.find(repo => repo.value === value.value);
      if (existingRepo) {
        setSelectedValue(existingRepo);
        setInputValue(existingRepo.value);
      }
    }
  }, [value?.value, repos]);

  return (
    <div>
      <Autocomplete
        options={repos}
        getOptionLabel={option => option.label}
        value={selectedValue}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        loading={loading}
        onChange={handleChange}
        autoHighlight
        disableClearable={false}
        filterOptions={options => options}
        ListboxComponent={props => (
          <ListboxComponent
            {...props}
            repos={repos}
            hasMore={hasMore}
            loading={loading}
            fetchRepos={fetchRepos}
          />
        )}
        ListboxProps={{
          style: { maxHeight: 300, overflow: 'auto' },
        }}
        renderOption={(props, option) => (
          <li {...props} key={option.value}>
            <div
              style={{ display: 'flex', alignItems: 'center', width: '100%' }}
            >
              <span>{option.label}</span>
              {/* {option.permissions && !(option.permissions.push || option.permissions.admin) && (
                <span style={{ color: '#B02828', marginLeft: 8 }}>(No write access)</span>
              )} */}
            </div>
          </li>
        )}
        renderInput={params => (
          <TextField
            {...params}
            label="Select Repository"
            variant="outlined"
            fullWidth
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress color="inherit" size={20} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </div>
  );
};
